import {
  UserKeys,
  getCurrentFolderName,
  getDriveHierarchy,
} from '../../../../api/users-api';
import { Doc, Folder } from '../../../../../types';
import { SetStateAction } from 'react';
import { InfiniteData, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DriveRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import { BreadcrumbItem } from './drive-breadcrumb-item';
import { isFeatureEnabled } from '../../../../utils/feature-flags';
import SharedToolbar from '../../../../shared/shared-toolbar-latest';
import SharedIconButton from '../../../../shared/buttons/shared-icon-button';

import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Skeleton from '@mui/material/Skeleton';

interface DriveGridToolbarProps {
  driveId: string;
  selectedFolder: string;
  selectedDocs: Doc[] | null;
  setSelectedFolder: (value: SetStateAction<string>) => void;
  setOpenTransferDialog: (value: SetStateAction<boolean>) => void;
  setOpenFolderDialog: (value: SetStateAction<boolean>) => void;
  setOpenAddShareDialog: (value: SetStateAction<boolean>) => void;
  setOpenMoveDialog: (value: SetStateAction<boolean>) => void;
  setOpenRestoreDialog: (value: SetStateAction<boolean>) => void;
  setOpenDeleteDialog: (value: SetStateAction<boolean>) => void;
  setOpenTrashDialog: (value: SetStateAction<boolean>) => void;
  setOpenRemoveLabelsDialog: (value: SetStateAction<boolean>) => void;
  setOpenQuickReports: (value: SetStateAction<boolean>) => void;
  allFolders: InfiniteData<Folder[], unknown> | undefined;
  driveRoles: string[];
  userEmail: string;
  features: Record<string, boolean> | null;
}

export default function DriveGridToolbar({
  driveId,
  selectedFolder,
  selectedDocs,
  setSelectedFolder,
  setOpenTransferDialog,
  setOpenFolderDialog,
  setOpenAddShareDialog,
  setOpenMoveDialog,
  setOpenRestoreDialog,
  setOpenDeleteDialog,
  setOpenTrashDialog,
  setOpenRemoveLabelsDialog,
  setOpenQuickReports,
  allFolders,
  driveRoles,
  userEmail,
  features,
}: DriveGridToolbarProps) {
  const queryClient = useQueryClient();

  const isSpecialFolder =
    selectedFolder === `${driveId}.shared` ||
    selectedFolder === `${driveId}.trash` ||
    selectedFolder === `${driveId}.starred`;

  const { data: hierarchy, isLoading } = useQuery({
    queryKey: ['driveHierarchy', driveId, selectedFolder, userEmail],
    queryFn: () => getDriveHierarchy(selectedFolder, userEmail),
    enabled: !isSpecialFolder && !!driveId && !!selectedFolder && !!userEmail,
  });

  const isFolderInAllFolders = allFolders?.pages
    .flatMap((page) => page)
    .some((folder) => folder.id === selectedFolder);

  const { data: folderName } = useQuery({
    queryKey: [UserKeys.DriveFolder, userEmail, selectedFolder],
    queryFn: () => getCurrentFolderName(userEmail, selectedFolder),
    enabled:
      !isSpecialFolder &&
      !isFolderInAllFolders &&
      !!userEmail &&
      !!selectedFolder &&
      selectedFolder !== driveId,
  });

  const selectedFolderName =
    selectedFolder === driveId
      ? 'My Drive'
      : selectedFolder === `${driveId}.shared`
      ? 'Shared with me'
      : selectedFolder === `${driveId}.trash`
      ? 'Trash'
      : selectedFolder === `${driveId}.starred`
      ? 'Starred'
      : allFolders?.pages
          .flatMap((page) => page)
          .find((folder) => folder.id === selectedFolder)?.title || folderName;

  function createBreadcrumbs() {
    if (isLoading && !isSpecialFolder) {
      return <Skeleton variant="text" width={200} />;
    }

    const breadcrumbItems = [];

    if (hierarchy && !isSpecialFolder) {
      breadcrumbItems.push(
        ...hierarchy.map((folder) => (
          <BreadcrumbItem
            key={folder.id}
            onClick={() => {
              if (folder.id === 'shared-with-me') {
                setSelectedFolder(`${driveId}.shared`);
              } else {
                setSelectedFolder(folder.id);
              }
            }}
          >
            {folder.name}
          </BreadcrumbItem>
        ))
      );
    }

    if (selectedFolderName) {
      breadcrumbItems.push(
        <BreadcrumbItem
          key={selectedFolder}
          onClick={
            !isSpecialFolder
              ? () => setSelectedFolder(selectedFolder)
              : undefined
          }
        >
          {selectedFolderName}
        </BreadcrumbItem>
      );
    }

    return breadcrumbItems;
  }

  const isAnySelectedShortcutDisabled =
    selectedDocs?.some(
      (doc) =>
        (doc.mimeType === 'application/vnd.google-apps.shortcut' &&
          doc.shortcutDetails?.targetTrashed) ||
        doc.shortcutDetails?.targetAccessible === false
    ) ?? false;

  const hasPublishedLabels =
    selectedDocs?.some((doc) => (doc.labelIds?.length ?? 0) > 0) ?? false;

  return (
    <SharedToolbar
      noColumn
      noFilter
      noQuickFilter
      leftPanel={
        <Box>
          <Breadcrumbs maxItems={4} separator="›" sx={{ maxWidth: 500 }}>
            {createBreadcrumbs()}
          </Breadcrumbs>
        </Box>
      }
      secondaryButtons={
        <>
          <SharedIconButton
            variant="transfer"
            onClick={() => {
              setOpenTransferDialog(true);
            }}
            disabled={
              !isAbleToSeeUIElement(
                driveRoles,
                DriveRoles.CanTransferDocs.value
              )
            }
            tooltipLabel="Transfer"
          />
          {selectedFolder === `${driveId}.trash` ||
          selectedFolder === `${driveId}.shared` ? null : (
            <SharedIconButton
              variant="createFolder"
              onClick={() => {
                setOpenFolderDialog(true);
              }}
              disabled={
                !isAbleToSeeUIElement(
                  driveRoles,
                  DriveRoles.CanCreateFolders.value
                )
              }
              tooltipLabel="Create Folder"
            />
          )}
          <SharedIconButton
            variant="share"
            onClick={() => {
              setOpenAddShareDialog(true);
            }}
            disabled={
              !selectedDocs ||
              !isAbleToSeeUIElement(
                driveRoles,
                DriveRoles.CanEditDocShares.value
              ) ||
              isAnySelectedShortcutDisabled
            }
            tooltipLabel="Add Share"
          />
          {selectedFolder === `${driveId}.trash` ||
          selectedFolder === `${driveId}.shared` ? null : (
            <SharedIconButton
              variant="move"
              onClick={() => {
                setOpenMoveDialog(true);
              }}
              disabled={
                !selectedDocs ||
                !isAbleToSeeUIElement(
                  driveRoles,
                  DriveRoles.CanMoveDocs.value
                ) ||
                isAnySelectedShortcutDisabled
              }
              tooltipLabel="Move"
            />
          )}
          {selectedFolder === `${driveId}.trash` ? (
            <SharedIconButton
              variant="restoreFromTrash"
              onClick={() => {
                setOpenRestoreDialog(true);
              }}
              disabled={
                !selectedDocs ||
                !isAbleToSeeUIElement(
                  driveRoles,
                  DriveRoles.CanRestoreDocs.value
                )
              }
              tooltipLabel="Restore"
            />
          ) : null}

          {isFeatureEnabled('driveLabelsOps', features) && (
            <SharedIconButton
              variant="label"
              onClick={() => {
                setOpenRemoveLabelsDialog(true);
              }}
              disabled={!selectedDocs || !hasPublishedLabels}
              tooltipLabel={
                hasPublishedLabels
                  ? 'Manage Labels'
                  : 'No labels on selected files'
              }
            />
          )}
          <SharedIconButton
            variant="quickReport"
            onClick={() => setOpenQuickReports(true)}
            disabled={!userEmail}
            tooltipLabel={
              userEmail
                ? 'Quick Reports'
                : 'Select a user to access Quick Reports'
            }
          />
          {selectedFolder === `${driveId}.trash` ? (
            <SharedIconButton
              variant="deleteForever"
              onClick={() => {
                setOpenDeleteDialog(true);
              }}
              disabled={
                !selectedDocs ||
                !isAbleToSeeUIElement(
                  driveRoles,
                  DriveRoles.CanDeleteDocs.value
                )
              }
              tooltipLabel="Delete Forever"
            />
          ) : (
            <SharedIconButton
              variant="delete"
              onClick={() => {
                setOpenTrashDialog(true);
              }}
              disabled={
                !selectedDocs ||
                !isAbleToSeeUIElement(driveRoles, DriveRoles.CanTrashDocs.value)
              }
              tooltipLabel="Move to Trash"
            />
          )}
          <SharedIconButton
            variant="refresh"
            onClick={() => {
              void queryClient.invalidateQueries({
                queryKey: [UserKeys.DriveDocuments],
              });
              void queryClient.invalidateQueries({
                queryKey: [UserKeys.DriveFolders],
              });
              void queryClient.invalidateQueries({
                queryKey: [UserKeys.DrivePermissions],
              });
              void queryClient.invalidateQueries({
                queryKey: [UserKeys.DriveAcls],
              });
              void queryClient.invalidateQueries({
                queryKey: [UserKeys.DriveLabels],
              });
            }}
            tooltipLabel="Refresh"
          />
        </>
      }
    />
  );
}
